/**
 * Today's review items in the work list (AIC-346, rebuilt to the approved 2026-09-30 prototype): each a row with the
 * initial tile, the action title, "Company · issue type", a one-line summary and "Review item →". Opening one expands
 * its detail directly after the row ("Context to check", the decision note, Resolve / Keep open for later); the full,
 * scoped item is loaded on demand, because a queue summary is never decision evidence. Resolving shows the completion
 * panel in the row's place: Undo, "n of m review items resolved", the next item for the same company, Back to queue.
 *
 * Notes live in this component's memory across closes and item switches for the page visit (never durable storage).
 * A decision needs an open item, can_resolve: true and an editable view (not a preview, not another analyst's queue).
 * A save or undo that lands after the view changed never replaces a newer selection: it reports by toast instead.
 * Resolving a review item never approves results.
 */
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client.ts";
import { describeError, useLoad, usePortal } from "../api/hooks.ts";
import type { Queue, ReviewItem } from "../api/schemas.ts";
import { READ_ONLY_MANAGER, READ_ONLY_PREVIEW, READ_ONLY_VIEW } from "../components/common.tsx";
import { TechnicalDetails, displayText } from "../components/displayText.tsx";
import { ButtonArrow, LineIcon } from "../components/LineIcon.tsx";
import { StateMessage } from "../components/states.tsx";
import { useToast } from "../components/Toast.tsx";
import { Link, useRoute } from "../router.tsx";
import { routeHref, withReturn } from "./navigationContext.ts";
import { ConfidentialMarker } from "./ReviewEvidence.tsx";
import { initialOf, plural, reviewCopy } from "./todayModel.ts";

type Summary = Queue["open_review_items"][number];
const DEFAULT_GUIDANCE = "Read the original answer and the reporting period. If the evidence is not enough, follow up with the vendor before resolving this item.";

/** Why an open item can't be decided here: a preview first, then a manager's chain, then a read-only view. */
function readOnlyCopy(canResolve: boolean | undefined, preview: boolean): string {
  if (preview) return READ_ONLY_PREVIEW;
  if (canResolve === false) return READ_ONLY_MANAGER;
  return READ_ONLY_VIEW;
}

/**
 * The interview's own words and the agent's evidence, behind a disclosure, with the way to the full record. The quote
 * says which words it is (the item's own, or the field's recorded answer) and where they came from; a confidential
 * quote or figure is marked (the Review page's ConfidentialMarker, by its rule) on the closed disclosure, the quote and
 * the evidence, so it is never read unmarked.
 */
function OriginalEvidence({ item }: { item: ReviewItem }) {
  const route = useRoute();
  if (!item.quote && !item.evidence && !item.assignment_id && !item.result_id) return null;
  const who = item.company ?? "this item";
  const quote = item.quote;
  const confidential = quote?.sensitive === true || item.figures?.sensitive === true;
  // Return to the Today view this item was opened from, with its filters ("/" is the Analyst Workbench, AIC-404).
  const origin = routeHref(route);
  return (
    <details className="today-evidence">
      <summary>Original evidence{confidential ? <ConfidentialMarker /> : null}</summary>
      {quote ? (
        <figure>
          <figcaption className="today-quote-of">{quote.of === "recorded_answer" ? "The vendor's recorded answer" : "The vendor's words in this item"}</figcaption>
          <blockquote>“{quote.text}”</blockquote>
          {quote.sensitive ? <ConfidentialMarker /> : null}
          <figcaption>{quote.source_label}{quote.clipped ? " · shortened; open the result for the full answer" : ""}</figcaption>
        </figure>
      ) : null}
      {item.evidence ? <><p className="today-evidence-note">Agent-attached evidence. It may summarise or quote the interview; its provenance is not independently established here.</p>{confidential ? <ConfidentialMarker /> : null}<blockquote>{displayText(item.evidence)}</blockquote><TechnicalDetails raw={item.evidence} shown={displayText(item.evidence)} /></> : null}
      {item.assignment_id || item.result_id ? (
        <p className="today-evidence-links">
          {item.assignment_id ? <Link className="ipd-link" to={withReturn(`/assignments/${item.assignment_id}`, origin)}>Open the assignment for {who}</Link> : null}
          {item.result_id ? <>{item.assignment_id ? " · " : ""}<Link className="ipd-link" to={withReturn(`/results/${item.result_id}`, origin)}>Open the result</Link></> : null}
        </p>
      ) : null}
    </details>
  );
}

/** The full, scoped item and its decision controls, loaded when the detail opens. */
function Decision({ id, summary, readOnly, note, onNote, onDone, onKeepOpen }: {
  id: string; summary: Summary; readOnly: boolean; note: string; onNote: (value: string) => void;
  onDone: (item: ReviewItem) => void; onKeepOpen: () => void;
}) {
  const { api, expire, preview = false } = usePortal();
  const toast = useToast();
  const load = useLoad((a) => a.reviewItems("all", { item: id }), [id]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pending = useRef(false);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const item = load.state === "ready" ? load.data.items.find((r) => r.id === id) : undefined;
  const copy = reviewCopy(summary);
  async function decide(value: string) {
    if (!item || item.status !== "open" || item.can_resolve !== true || readOnly || preview || pending.current) return;
    pending.current = true; setBusy(true); setError(null);
    try {
      await api.resolveReviewItem(id, "resolved", value);
      onDone(item);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) expire();
      const failure = e instanceof Error ? e : new Error(String(e));
      if (live.current) setError(failure);
      else toast.show({ tone: "err", title: "The review item wasn't updated", desc: describeError(failure) });
    } finally { pending.current = false; if (live.current) setBusy(false); }
  }
  if (load.state === "loading") return <p className="today-detail-wait" role="status">Loading the review evidence…</p>;
  if (load.state === "error") return <StateMessage tone="risk" size="sm" title="Evidence didn't load." actions={<button type="button" className="ipd-btn" onClick={load.reload}>Retry evidence</button>}>{describeError(load.error)} Nothing was decided.</StateMessage>;
  if (!item) return <p className="today-detail-wait" role="status">This item is no longer available in your review scope. <Link className="ipd-link" to="/review">Open the review queue</Link></p>;
  const noteId = `note-${id}`;
  const editable = item.status === "open" && item.can_resolve === true && !readOnly && !preview;
  return (
    <>
      <OriginalEvidence item={item} />
      {item.status !== "open" ? (
        <p className="today-detail-closed">This review item was {item.status === "resolved" || item.status === "dismissed" ? item.status : "closed"} {item.resolved_by ? `by ${item.resolved_by}` : "automatically"}.{item.resolution_note ? ` Note: ${item.resolution_note}` : ""}</p>
      ) : !editable ? (
        <p className="ipd-notice ipd-notice--calm today-readonly-note"><span>{readOnlyCopy(item.can_resolve, preview)}</span></p>
      ) : (
        <>
          <label className="today-note-label" htmlFor={noteId}>Decision note <span>(optional)</span><span className="ipd-sr"> for {summary.company}</span></label>
          <textarea id={noteId} className="today-note" value={note} maxLength={2000} placeholder="Explain how you checked this item…" onChange={(e) => onNote(e.target.value)} />
          <p className="today-note-status" role="status">{note.trim() ? "Draft note kept while this page is open." : "Notes stay on this page when you close or switch items."}</p>
          {error ? <StateMessage tone="risk" size="sm" title="The item wasn't updated.">{describeError(error)} It's still open, and your note is kept.</StateMessage> : null}
          <div className="today-detail-actions">
            <button type="button" className="ipd-btn ipd-btn--go" aria-disabled={busy || undefined} aria-busy={busy || undefined} aria-label={`Resolve: ${copy.title} — ${summary.company}`} onClick={() => { if (!busy) void decide(note.trim()); }}>Resolve</button>
            <button type="button" className="ipd-btn" onClick={onKeepOpen}>Keep open for later</button>
          </div>
          <small className="today-detail-foot">Resolving this review item does not approve interview results.</small>
        </>
      )}
    </>
  );
}

export interface TodayReviewsProps {
  /** The review items in view, prerequisites first. */
  items: Summary[];
  /** False while another tab is shown or the list is loading: nothing renders, notes and the completion stay. */
  visible: boolean;
  selected: string | null;
  onSelect: (id: string | null) => void;
  viewKey: string;
  readOnly: boolean;
  /** A decision was saved (closed) or undone; Today refreshes the queue and keeps the session's progress. */
  onSettled: (id: string, closed: boolean, company: string) => void;
  /** How many of this company's items were resolved on this page visit. */
  resolvedFor: (company: string) => number;
  /** The decided item's results row (by its assignment), once no review item is left for the company (Review results). */
  resultsFor: (item: ReviewItem) => { href: string } | undefined;
  /** Review next item: Today focuses the company's reviews and opens the item. */
  onContinue: (next: Summary) => void;
  /** Back to queue (keeps the view) or Back to all work (clears the company, search and tab). */
  onBack: (allWork: boolean) => void;
  onCompletion?: (shown: boolean) => void;
}

export function TodayReviews({ items, visible, selected, onSelect, readOnly, onSettled, viewKey, resolvedFor, resultsFor, onContinue, onBack, onCompletion }: TodayReviewsProps) {
  const { api, expire } = usePortal();
  const toast = useToast();
  const currentView = useRef(viewKey);
  currentView.current = viewKey;
  const currentSelection = useRef(selected);
  currentSelection.current = selected;
  const visibleItems = useRef(items);
  visibleItems.current = items;
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [done, setDone] = useState<{ item: ReviewItem; at: number } | null>(null);
  const currentDone = useRef(done);
  currentDone.current = done;
  const [undoing, setUndoing] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<Error | null>(null);
  const completion = useRef<HTMLElement>(null);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  useEffect(() => { if (done) completion.current?.focus({ preventScroll: true }); completion.current?.scrollIntoView?.({ block: "nearest" }); }, [done]);
  useEffect(() => { onCompletion?.(!!done && visible); }, [done, visible, onCompletion]);
  // Opening any item (including from Today's suggestion) replaces the previous decision's completion panel.
  useEffect(() => { if (selected && currentDone.current) setDone(null); }, [selected]);
  // Opening moves focus into the detail; closing returns it to the row's button.
  const focused = useRef<string | null>(null);
  useEffect(() => {
    const was = focused.current;
    if (!selected) {
      focused.current = null;
      if (was && !currentDone.current) document.getElementById(`review-btn-${was}`)?.focus({ preventScroll: true });
      return;
    }
    if (was === selected) return;
    const element = document.getElementById(`evidence-${selected}`);
    if (element) { element.focus({ preventScroll: true }); element.scrollIntoView?.({ block: "nearest" }); focused.current = selected; }
  }, [selected, items, visible]);

  async function undo() {
    if (!done || pending.current) return;
    const origin = done.item.id;
    const originView = currentView.current;
    const selectionAtStart = currentSelection.current;
    pending.current = true; setUndoing(true); setError(null);
    try {
      await api.reopenReviewItem(done.item.id);
      if (live.current) {
        onSettled(origin, false, done.item.company ?? "");
        const unchanged = currentSelection.current === selectionAtStart && currentView.current === originView;
        // The decision is reopened either way, so its completion panel (and its Undo) must not outlive it.
        if (currentDone.current?.item.id === origin) setDone(null);
        if (unchanged) onSelect(origin);
        else toast.show({ tone: "ok", title: "Review decision undone", desc: `${done.item.company}'s item is open again.` });
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) expire();
      if (live.current) setError(e instanceof Error ? e : new Error(String(e)));
    } finally { pending.current = false; if (live.current) setUndoing(false); }
  }

  if (!visible) return null;
  const rows = done ? items.filter((r) => r.id !== done.item.id) : items;
  let panel = null;
  if (done) {
    const company = done.item.company ?? "this company";
    const remaining = rows.filter((r) => r.company === done.item.company);
    const next = remaining[0];
    const results = next ? undefined : resultsFor(done.item);
    const resolvedCount = Math.max(1, resolvedFor(company));
    const total = resolvedCount + remaining.length;
    const title = remaining.length ? "Review item resolved" : results ? "Review items cleared. Results are ready to review." : "All review items cleared for this company";
    const description = remaining.length
      ? `${remaining.length} ${plural(remaining.length, "review item remains", "review items remain")} for ${company}. Keep going with the same company.`
      : results ? `You've cleared the open review items for ${company}. Review the full interview results before approving them.`
        : `No open review items remain for ${company}. This does not approve interview results.`;
    panel = (
      <section key="done" className="today-complete" ref={completion} tabIndex={-1} aria-labelledby="today-complete-h">
        <div className="today-complete-head">
          <span className="today-complete-check" aria-hidden="true"><LineIcon name="check" /></span>
          <div><p className="today-complete-company">{company}</p><h2 id="today-complete-h">{title}</h2></div>
          <button type="button" className="ipd-btn" disabled={undoing} aria-label="Undo decision" onClick={() => void undo()}>{undoing ? "Undoing…" : "Undo"}</button>
        </div>
        <p className="today-complete-desc" role="status">{description}</p>
        <div className="today-complete-progress"><span>{resolvedCount} of {total} review {plural(total, "item", "items")} resolved</span><span>This page visit</span></div>
        {next || results ? <div className="today-complete-next"><span>{results ? "Unlocked next step" : "Up next"}</span><strong>{next ? reviewCopy(next).title : "Review interview results"}</strong></div> : null}
        <div className="today-complete-actions">
          {next ? <button type="button" className="ipd-btn ipd-btn--go" onClick={() => { setDone(null); onContinue(next); }}>Review next item<ButtonArrow /></button>
            : results ? <Link className="ipd-btn ipd-btn--go" to={results.href}>Review results<ButtonArrow /></Link> : null}
          <button type="button" className="ipd-btn" onClick={() => { setDone(null); onBack(!next && !results); }}>{next || results ? "Back to queue" : "Back to all work"}</button>
        </div>
        {error ? <p className="today-complete-error" role="alert">Couldn't undo. {describeError(error)} <button type="button" className="ipd-link" onClick={() => void undo()}>Retry undo</button></p> : null}
        <p className="today-complete-note">This decision is saved{notes[done.item.id]?.trim() ? " with your note" : ""}. The underlying figures still require separate approval.</p>
      </section>
    );
  }
  const out = rows.map((r, i) => {
    const copy = reviewCopy(r);
    const open = selected === r.id;
    const note = notes[r.id] ?? "";
    return [
      done && i === done.at ? panel : null,
      <article key={r.id} className={`today-row${open ? " is-open" : ""}`}>
        <span className="today-tile" aria-hidden="true">{initialOf(r.company)}</span>
        <div className="today-row-copy">
          <h3>{copy.title}</h3>
          <p className="today-row-meta"><b>{r.company}</b><span aria-hidden="true">·</span><span>{copy.meta}</span></p>
          <p className="today-row-line">{copy.line}</p>
          {note.trim() && !open ? <span className="today-row-saved">Draft note kept on this page</span> : null}
        </div>
        <button type="button" id={`review-btn-${r.id}`} className="ipd-btn today-row-act" aria-expanded={open} aria-controls={`evidence-${r.id}`} aria-label={`${copy.verb}: ${copy.title} — ${r.company}`}
          onClick={() => { onSelect(open ? null : r.id); setDone(null); }}>{copy.verb}<ButtonArrow /></button>
      </article>,
      open ? (
        <section key={`${r.id}-detail`} id={`evidence-${r.id}`} className="today-detail" tabIndex={-1} aria-labelledby={`detail-h-${r.id}`}>
          <div className="today-detail-top"><span>{r.company} · {copy.meta}</span><button type="button" className="ipd-btn" onClick={() => onSelect(null)}>Close details</button></div>
          <h2 id={`detail-h-${r.id}`}>{copy.title}</h2>
          <p className="today-detail-body">{displayText(r.reason)}</p>
          <TechnicalDetails raw={r.reason} shown={displayText(r.reason)} />
          <div className="today-source"><b>Context to check</b><p>{r.guidance ?? DEFAULT_GUIDANCE}</p></div>
          <Decision key={r.id} id={r.id} summary={r} readOnly={readOnly} note={note} onNote={(value) => setNotes((n) => ({ ...n, [r.id]: value }))} onKeepOpen={() => onSelect(null)}
            onDone={(item) => {
              if (!live.current) return;
              onSettled(item.id, true, item.company ?? r.company);
              const at = visibleItems.current.findIndex((x) => x.id === item.id);
              if (currentSelection.current === item.id && at >= 0) { setDone({ item: { ...item, company: item.company ?? r.company }, at }); onSelect(null); }
              else toast.show({ tone: "ok", title: "Review item resolved", desc: `${item.company ?? r.company}'s decision was saved.` });
            }} />
        </section>
      ) : null,
    ];
  }).flat().filter(Boolean);
  // The completion panel takes the decided row's place; after the last row when it was the last.
  if (done && done.at >= rows.length) out.push(panel);
  return <>{out}</>;
}
